import { useRequest, APIError } from "@certn/api";
import { showToast } from "@certn/cascadia";
import {
  BasicButton,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Modal,
  PrimaryButton,
  TextField,
} from "@certn/cascadia-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  invoiceId: string;
  currentPONumber: string;
  onClose: () => void;
  onSuccess?: () => void;
}

export function UpdatePONumberModal(props: Props) {
  const { t } = useTranslation();
  const request = useRequest();
  const { invoiceId, currentPONumber, onClose, onSuccess } = props;
  const [poNumber, setPONumber] = useState(currentPONumber);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const trimmed = poNumber.trim();
      await request(
        `/api/internal/client-portal/invoices/${invoiceId}/update-po-number/`,
        {
          method: "POST",
          body: JSON.stringify({ po_number: trimmed || null }),
        }
      );
      showToast(t("PO number updated successfully"), { type: "success" });
      onSuccess?.();
      onClose();
    } catch (error) {
      const message =
        error instanceof APIError && error.detail
          ? error.detail
          : t("Unable to update PO number");
      showToast(message, { type: "error" });
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen>
      <Dialog>
        <DialogHeader onClose={onClose}>{t("Update PO number")}</DialogHeader>
        <DialogContent>
          <TextField
            label={t("PO number")}
            value={poNumber}
            onChange={(value) => setPONumber(String(value))}
            testID="update-po-number-input"
          />
        </DialogContent>
        <DialogFooter>
          <BasicButton
            onPress={onClose}
            isDisabled={isSubmitting}
            testID="update-po-number-modal-cancel-button"
          >
            {t("Cancel")}
          </BasicButton>
          <PrimaryButton
            onPress={handleConfirm}
            isDisabled={isSubmitting}
            isLoading={isSubmitting}
            testID="update-po-number-modal-confirm-button"
          >
            {t("Save")}
          </PrimaryButton>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
